import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Catalog, CartLineItem, RefillProduct } from '@cureskin/api-client/src/server-api';
import { ConnectionService } from '@services/connection-service';
import { ExperimentService } from '@services/experiment/experiment.service';
import { EventLoggerService } from '@services/event-logger-service';
import { CartService } from '@services/cart-service/cart.service';
import { BroadcastService } from '@services/broadcast-service';
import { UserService } from '@services/user/user.service';
import { AppConfig } from 'client/app/app.config';
import { CsIconModule } from '@components/icon';
import { HorizontalProductCardComponent, ProductCardItem } from '@shared/horizontal-product-card/horizontal-product-card.component';
import { CartUpdateOptions } from '@shared/types/cart.types';
import * as fromSelectors from '@store/selectors/app.selectors';
import * as fromActions from '@store/actions/app.actions';

interface RefillCardItem { daysSincePurchase: number; product: ProductCardItem; }

@Component({
  selector: 'app-restock-banner',
  templateUrl: './restock-banner.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, CsIconModule, HorizontalProductCardComponent],
})
export class RestockBannerComponent implements OnInit {
  private readonly conn = inject(ConnectionService);
  private readonly experimentService = inject(ExperimentService);
  private readonly eventLogger = inject(EventLoggerService);
  private readonly cartService = inject(CartService);
  private readonly broadcastService = inject(BroadcastService);
  private readonly userService = inject(UserService);
  private readonly store = inject(Store);
  private readonly appConfig = inject(AppConfig);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  readonly products = signal<RefillCardItem[]>([]);
  readonly cartQuantities = signal<Record<string, number>>({});
  // Emits the resolved content visibility once, so a parent can decide whether to render this banner's wrapper.
  readonly visibilityChange = output<boolean>();
  private cartItems: CartLineItem[] = [];
  private isCartOperationPending: boolean = false;

  async ngOnInit(): Promise<void> {
    await this.experimentService.experimentsLoaded;
    if (!this.experimentService.restockBannerExperiment) return this.visibilityChange.emit(false);
    const user = this.conn.getActingUser();
    if (!user || !this.userService.isPaidUser(user)) return this.visibilityChange.emit(false);

    const items: RefillProduct[] = await this.conn.getRefillProducts();
    if (!items.length) return this.visibilityChange.emit(false);
    this.products.set(items.map((item: RefillProduct): RefillCardItem => ({
      ...item,
      product: { ...item.product, image: this.resolveImage(item.product) },
    })));
    this.visibilityChange.emit(true);

    await this.loadCart();
    this.subscribeToCartChanges();
  }

  getQuantity(item: RefillCardItem): number {
    return this.cartQuantities()[item.product?.objectId ?? ''] ?? 0;
  }

  async onAdd(product: ProductCardItem): Promise<void> {
    await this.changeCart(product, this.appConfig.Shared.Cart.Action.ADD);
  }

  async onRemove(product: ProductCardItem): Promise<void> {
    await this.changeCart(product, this.appConfig.Shared.Cart.Action.REMOVE);
  }

  viewProductDetail(product: ProductCardItem): void {
    if (!product?.objectId) return;
    this.logClick('view-from-restock', product.objectId);
    this.router.navigate(['product', product.objectId]);
  }

  viewAllRefill(): void {
    this.logClick('view-all-from-restock');
    this.router.navigate(['/user/order/reorder'], { queryParams: { from: this.appConfig.Shared.ViewAllPage.queryParams.REFILL, source: 'home' } });
  }

  private logClick(name: string, productId?: string): void {
    this.eventLogger.cleverTapEvent('click', JSON.stringify({ name, pageName: 'home', productId }));
  }

  private async changeCart(product: ProductCardItem, action: string): Promise<void> {
    const objectId = product?.objectId;
    if (!objectId || this.isCartOperationPending) return;
    this.isCartOperationPending = true;
    const isAdd = action === this.appConfig.Shared.Cart.Action.ADD;
    this.logClick(isAdd ? 'add-to-cart-from-restock' : 'remove-from-cart-from-restock', objectId);
    try {
      const options: CartUpdateOptions = {
        productsInCart: { ...this.cartQuantities() },
        cartItems: this.cartItems,
        section: 'restockBanner',
      };
      const result = await this.cartService.addOrRemoveFromCart(product, action, options);
      if (!result.success) return;

      const quantities = { ...this.cartQuantities() };
      if (result.updatedQuantity > 0) quantities[objectId] = result.updatedQuantity;
      else delete quantities[objectId];
      this.cartQuantities.set(quantities);

      this.store.dispatch(fromActions.CartUpdateProductsBegin({ products: quantities }));
      this.broadcastService.broadcast('REFRESH_SHOP_CART');
    } finally {
      this.isCartOperationPending = false;
    }
  }

  private async loadCart(): Promise<void> {
    const cart = await this.conn.fetchNewCart(this.conn.getActingUser()?.id);
    this.cartItems = cart?.data?.lineItems ?? [];
    const quantities: Record<string, number> = {};
    this.cartItems.forEach((item: CartLineItem): void => {
      if (item.productId) quantities[item.productId] = item.quantity ?? 0;
    });
    this.cartQuantities.set(quantities);
  }

  private subscribeToCartChanges(): void {
    this.store.select(fromSelectors.selectCartProducts)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((products: Record<string, number>): void => { this.cartQuantities.set({ ...products }); });
    this.broadcastService.on('REFRESH_SHOP_CART')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((): void => { this.loadCart(); });
  }

  private resolveImage(product: Catalog): string {
    const first = (value: string | string[] | undefined): string => {
      if (Array.isArray(value)) return value[0] ?? '';
      return value ?? '';
    };
    return first(product?.rebrandedImageWithBackground)
      || first(product?.rebrandedImageWithoutBackground)
      || first(product?.productUnboxedImage)
      || '';
  }
}
